import * as React from 'react';

import { HonestWork } from '@cian/honestwork-component';
import { ArrowDownS16, ArrowUpS16 } from '@cian/ui-kit/prev';
import { Button } from '@cian/ui-kit';
import { IHttpApi } from '@cian/http-api/shared';
import { ILogger } from '@cian/logger';

import { IVacancy } from '../../../index';
import { trackingEditVacancy, trackingShowContacts, trackingShowFullDescription } from '../../../tracking/tracking';
import { decodeHtml, scrollTop } from '../../../utils';
import { employmentTypes, formatExperience, formatPhone, formatSalary } from '../../vacancy_preview/vacancy_preview';
import { trackUpdateVacancyDateClick } from './tracking';

import { RefreshIcon } from './refresh_icon';
import { IUser } from '../../../types/user';

import { updateVacancyDate } from '../../../services/updateVacancyDate';
import { ESubdomain } from '../../../constants/domain';

import * as styles from './vacancy.css';
interface IVacancyProps {
  vacancy: IVacancy;
  user: IUser;
  editBackUrl: string;
  logger: ILogger;
  httpApi: IHttpApi;
  onDelete(id: number): void;
}

interface IVacancyState {
  isVisibleContacts: boolean;
  isFullDescription: boolean;
  isDateRefreshing: boolean;
}

interface IAuthorProps {
  author?: {
    id: number;
    age?: string;
    avatarUrl?: string;
    isTrusted?: boolean;
    isProfi?: boolean;
    name?: string;
    profileUrl?: string;
  };
}

const Author = (props: IAuthorProps) => {
  const { author } = props;

  if (!author) {
    return <div />;
  } else if (!author.name) {
    return <div className={styles['author']}>ID:{author.id}</div>;
  }

  return (
    <div className={styles['author']}>
      {!author.profileUrl && <div>{author.name}</div>}
      {author.profileUrl && (
        <a className={styles['author-link']} href={`/${author.profileUrl}`}>
          {author.name}
        </a>
      )}
      <div>
        {author.isTrusted && <HonestWork className={styles['honest']} />}
        <div className={styles['badge-pro']} />
      </div>
      {author.age && <div className={styles['author-time_in_cian']}>{author.age} на&nbsp;ЦИАН</div>}
      {author.avatarUrl && <img src={author.avatarUrl} className={styles['author-logo']} />}
    </div>
  );
};

export class Vacancy extends React.Component<IVacancyProps, IVacancyState> {
  public constructor(props: IVacancyProps) {
    super(props);

    this.state = {
      isDateRefreshing: false,
      isFullDescription: false,
      isVisibleContacts: false,
    };
  }

  public render() {
    const { vacancy } = this.props;
    const isVacancyCreator = this.props.user.id === vacancy.userId;
    const couldNotBeUpdated = !!vacancy.leftToUpdate;
    const { isDateRefreshing } = this.state;

    return (
      <div id={String(vacancy.id)} className={styles['vacancy']}>
        <div className={styles['content']}>
          <div className={styles['vacancy_info']}>
            <div className={styles['title']}>{decodeHtml(vacancy.specialization)}</div>
            <div>
              <div className={styles['info_item']}>
                <div className={styles['light_text']}>Зарплата</div>
                <div className={styles['bold_text']}>{formatSalary(vacancy.minSalary, vacancy.maxSalary)}</div>
              </div>
              <div className={styles['info_item']}>
                <div className={styles['light_text']}>Город</div>
                <div className={styles['bold_text']}>{vacancy.region.displayName}</div>
              </div>
              <div className={styles['info_item']}>
                <div className={styles['light_text']}>Опыт</div>
                <div className={styles['bold_text']}>{formatExperience(vacancy.workExperience)}</div>
              </div>
              <div className={styles['info_item']}>
                <div className={styles['light_text']}>Занятость</div>
                <div className={styles['bold_text']}>{employmentTypes[vacancy.employment]}</div>
              </div>
            </div>
            <div className={styles['description']}>{decodeHtml(this.getDescriptionText(vacancy.description))}</div>
            {vacancy.description.length > 300 && (
              <div className={styles['collapsed_btn-container']}>
                <button type="button" className={styles['collapsed_btn']} onClick={this.showDescription}>
                  {this.state.isFullDescription ? 'Свернуть описание' : 'Развернуть описание'}
                  <span className={styles['icon']}>
                    {this.state.isFullDescription ? (
                      <ArrowUpS16 color="primary_100" />
                    ) : (
                      <ArrowDownS16 color="primary_100" />
                    )}
                  </span>
                </button>
              </div>
            )}
          </div>
          <Author author={vacancy.company} />
        </div>
        <div className={styles['footer']}>
          <div>
            {!isVacancyCreator && !this.state.isVisibleContacts && (
              <Button data-name="ShowContactsButton" size="XS" theme="fill_primary" onClick={this.showContacts}>
                Показать контакты
              </Button>
            )}
            {(isVacancyCreator || this.state.isVisibleContacts) && (
              <span>
                {vacancy.contactPhone && <div className={styles['phone']}>{formatPhone(vacancy.contactPhone)}</div>}
                {vacancy.contactEmail && <div className={styles['email']}>{vacancy.contactEmail}</div>}
              </span>
            )}
            {isVacancyCreator && (
              <div className={styles['edit_btn-container']}>
                <Button data-name="EditButton" size="XS" theme="stroke_primary" onClick={this.editVacancy}>
                  Редактировать
                </Button>
                <Button
                  data-name="DeleteButton"
                  size="XS"
                  theme="stroke_primary"
                  onClick={() => this.props.onDelete(vacancy.id)}
                >
                  Удалить
                </Button>
              </div>
            )}
          </div>
          <div className={styles['date-container']}>
            {isVacancyCreator && (
              <Button
                type="button"
                theme="fill_primary"
                onClick={this.handleRefreshDate}
                disabled={couldNotBeUpdated || isDateRefreshing}
                beforeIcon={!isDateRefreshing ? <RefreshIcon /> : undefined}
                loading={isDateRefreshing}
              />
            )}

            {isVacancyCreator && couldNotBeUpdated && (
              <div className={styles['refresh_date-countdown']}>
                Обновить дату можно будет через {vacancy.leftToUpdate}
              </div>
            )}

            <span className={styles['created_date']}>{vacancy.updatedHumanized}</span>
          </div>
        </div>
      </div>
    );
  }

  private getDescriptionText(description: string) {
    return description.length > 300 && !this.state.isFullDescription ? `${description.slice(0, 300)}...` : description;
  }

  private showDescription = () => {
    if (!this.state.isFullDescription) {
      trackingShowFullDescription(this.props.vacancy.id);
    }
    this.setState({
      isFullDescription: !this.state.isFullDescription,
    });
  };

  private editVacancy = () => {
    trackingEditVacancy(this.props.vacancy.id);
    window.location.href = `/vacancies/edit/${this.props.vacancy.id}?back_url=${encodeURIComponent(
      this.props.editBackUrl,
    )}`;
  };

  private showContacts = () => {
    trackingShowContacts(this.props.vacancy.id);
    this.setState({
      isVisibleContacts: true,
    });
  };

  private handleRefreshDate = () => {
    const { httpApi, logger, vacancy } = this.props;

    this.setState({ isDateRefreshing: true });

    trackUpdateVacancyDateClick(vacancy.id);

    updateVacancyDate({ httpApi, logger, subdomain: ESubdomain.Vacancies }, { vacancyId: vacancy.id })
      .then(() => {
        scrollTop();

        window.location.href = this.props.editBackUrl;
      })
      .catch(() => {
        this.setState({ isDateRefreshing: false });
      });
  };
}
